import { useTranslation } from 'react-i18next'
import type { EditableThemeTokensByVariant } from '@/shared/theme/theme-config'
import { useCustomThemeDraft } from '@/features/theme/model/use-custom-theme-draft'
import {
  CustomThemeActions,
  CustomThemeEditor,
  CustomThemeError,
  CustomThemeFooter,
} from '@/features/theme/ui/custom-theme-editor'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet'

export function CustomThemeSheet({
  open,
  onOpenChange,
  editableTokens,
  onSave,
  onReset,
  onSetCustomPreset,
  canConfigure,
  isSaving,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  editableTokens: EditableThemeTokensByVariant
  onSave: (draft: EditableThemeTokensByVariant) => Promise<void>
  onReset: () => Promise<void>
  onSetCustomPreset: () => void
  canConfigure: boolean
  isSaving: boolean
}) {
  const { t } = useTranslation('theme')
  const {
    draft,
    error,
    randomizeDraft,
    resetDraft,
    updateDraftToken,
    validateDraft,
  } = useCustomThemeDraft({
    editableTokens,
  })

  const handleSave = async () => {
    if (!validateDraft()) {
      return
    }

    await onSave(draft)
    onSetCustomPreset()
    onOpenChange(false)
  }

  const handleReset = async () => {
    await onReset()
    resetDraft()
    onSetCustomPreset()
  }

  const handleRandomize = () => {
    onSetCustomPreset()
    randomizeDraft()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle>{t('theme:sheetTitle')}</SheetTitle>
          <SheetDescription>{t('theme:sheetDescription')}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <CustomThemeActions
            canConfigure={canConfigure}
            onRandomize={handleRandomize}
            onReset={handleReset}
          />
          <CustomThemeError error={error} />
          <CustomThemeEditor draft={draft} onTokenChange={updateDraftToken} />
        </div>

        <CustomThemeFooter
          canConfigure={canConfigure}
          isSaving={isSaving}
          onCancel={() => onOpenChange(false)}
          onSave={handleSave}
        />
      </SheetContent>
    </Sheet>
  )
}
